import { InputRef } from '@unistocks/components/basic/input';
import { DatePickerInputRef } from '@unistocks/components/date-picker/date-picker/types';
import { Form } from '@unistocks/components/form/form';
import { arrayTransform } from '@unistocks/components/form/validation-utils';
import { usePortfolios } from '@unistocks/features/dashboard/hooks/use-portfolios';
import { SavingsGoalPreview } from '@unistocks/features/savings-goals/components/chart/savings-goal-preview';
import {
  annualReturnsTransform,
  savingsGoalSchemaFactory
} from '@unistocks/features/savings-goals/components/forms/schema';
import {
  ContributionType,
  ListResultDtoOfLookupDtoOfGuid,
  SavingsGoalCreateOrUpdateDto
} from '@unistocks/proxy/axios-client';
import { usePortfoliosGetListQuery } from '@unistocks/proxy/axios-client/PortfoliosQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import { extractOptionValue } from '@unistocks/shared/utils/list-utils';
import { createDate, today } from '@unistocks/shared/utils/util';
import { SelectOption } from '@unistocks/types';
import { FormPropsBase } from '@unistocks/types/form-props';
import { useCallback, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Separator, TamaguiElement } from 'tamagui';

export const CreateSavingsGoalForm: React.FC<FormPropsBase<SavingsGoalCreateOrUpdateDto>> = ({
  isSubmitting,
  onSubmit
}) => {
  const schema = savingsGoalSchemaFactory();

  const initialValues: Partial<SavingsGoalCreateOrUpdateDto> = {
    name: '',
    portfolios: [],
    contributionType: ContributionType.MONTHLY_END,
    currency: null,
    startDate: today().toISODate(),
    annualReturns: 0.1
  };

  return (
    <Form<SavingsGoalCreateOrUpdateDto>
      formId="create-savings-goal"
      schema={schema}
      initialValues={initialValues}
      isLoading={isSubmitting}
      onSubmit={onSubmit}
      triggerText={t('::Add')}
    >
      <CreateSavingsGoalFormContent />
    </Form>
  );
};

CreateSavingsGoalForm.displayName = 'CreateSavingsGoalForm';

const CreateSavingsGoalFormContent: React.FC = () => {
  const { setValue } = useFormContext<SavingsGoalCreateOrUpdateDto>();
  const nameRef = useRef<InputRef>(null);
  const goalValueRef = useRef<InputRef>(null);
  const portfoliosRef = useRef<TamaguiElement>(null);
  const currencyRef = useRef<TamaguiElement>(null);
  const startDateRef = useRef<DatePickerInputRef>(null);
  const endDateRef = useRef<DatePickerInputRef>(null);
  const contributionRef = useRef<InputRef>(null);
  const annualReturnsRef = useRef<InputRef>(null);

  const formValues = useWatch<SavingsGoalCreateOrUpdateDto>();
  const { portfolios: portfolioIds = [], currency, startDate } = formValues;

  const { data: portfolios } = usePortfoliosGetListQuery({
    select: useCallback(
      ({ items }: ListResultDtoOfLookupDtoOfGuid) =>
        items.map(({ id, displayName }) => ({ key: displayName, value: id })),
      []
    )
  });

  const { currencyOptions, minDate } = usePortfolios(portfolioIds, undefined, res => {
    const currencies = res.reduce(
      (acc, { data, isSuccess }) => (isSuccess ? acc.add(data.currency) : acc),
      new Set<string>()
    );

    const currencyOptions = Array.from(currencies).map(
      c => ({ key: t(`::Currency:${c}`), value: c }) as SelectOption
    );

    const minDate = res
      .reduce(
        (acc, { data, isSuccess }) =>
          isSuccess && createDate(data.creationTime) < acc ? createDate(data.creationTime) : acc,
        today()
      )
      .toISODate();

    return {
      currencyOptions,
      minDate
    };
  });

  useEffect(() => {
    if (currencyOptions.length < 2 && currency !== currencyOptions[0]?.value) {
      setValue('currency', currencyOptions[0]?.value ?? null);
    }
  }, [currency, currencyOptions, setValue]);

  return (
    <>
      <SavingsGoalPreview {...(formValues as SavingsGoalCreateOrUpdateDto)} />
      <Separator my={'$4'} />
      <Form.Input
        ref={nameRef}
        name={'name'}
        label={t('::SavingsGoal:Name')}
        placeholder={t('::SavingsGoal:Name:Placeholder')}
        helpText={t('::SavingsGoal:Name:HelpText')}
        isRequired
        returnKeyType="next"
        onSubmitEditing={() => goalValueRef.current?.focus()}
      />
      <Form.Input
        ref={goalValueRef}
        name={'goalValue'}
        label={t('::SavingsGoal:Amount')}
        helpText={t('::SavingsGoal:Amount:HelpText')}
        appendText={currency ?? ''}
        isRequired
        textContentType="none"
        keyboardType="numeric"
        returnKeyType="next"
        onSubmitEditing={() => portfoliosRef.current?.focus()}
      />
      <Form.Select
        ref={portfoliosRef}
        name={'portfolios'}
        label={t('::Portfolios')}
        placeholder={t('::SelectWhat', { 0: t('::Portfolios').toLocaleLowerCase() })}
        helpText={t('::SavingsGoal:Portfolios:HelpText')}
        options={portfolios ?? []}
        multiple
        isRequired
        keyExtractor={extractOptionValue}
        valueTransform={arrayTransform}
      />
      <Form.Select
        ref={currencyRef}
        name={'currency'}
        label={t('::DisplayName:Currency')}
        placeholder={t('::SelectWhat', {
          0: t('::DisplayName:Currency').toLocaleLowerCase()
        })}
        helpText={t('::SavingsGoal:Currency:HelpText')}
        options={currencyOptions}
        hidden={currencyOptions.length < 2}
        keyExtractor={extractOptionValue}
        isRequired
      />
      <Form.Datepicker
        ref={startDateRef}
        name={'startDate'}
        label={t('::SavingsGoal:StartDate')}
        helpText={t('::SavingsGoal:StartDate:HelpText')}
        minDate={minDate}
        isRequired
      />
      <Form.Datepicker
        ref={endDateRef}
        name={'endDate'}
        label={t('::SavingsGoal:EndDate')}
        helpText={t('::SavingsGoal:EndDate:HelpText')}
        minDate={startDate}
        isRequired
      />
      <Form.Input
        ref={contributionRef}
        name={'contribution'}
        label={t('::SavingsGoal:MonthlyContribution')}
        placeholder={t('::SavingsGoal:Contribution:Amount:Placeholder')}
        helpText={t('::SavingsGoal:Contribution:Amount:HelpText')}
        appendText={currency ?? ''}
        textContentType="none"
        keyboardType="numeric"
        returnKeyType="next"
        onSubmitEditing={() => annualReturnsRef.current?.focus()}
      />
      <Form.Input
        ref={annualReturnsRef}
        name={'annualReturns'}
        label={t('::SavingsGoal:AnnualReturns')}
        helpText={t('::SavingsGoal:AnnualReturns:HelpText')}
        appendText="%"
        textContentType="none"
        keyboardType="numeric"
        returnKeyType="done"
        valueTransform={annualReturnsTransform}
      />
    </>
  );
};

CreateSavingsGoalFormContent.displayName = 'CreateSavingsGoalFormContent';
